import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { Permission } from 'src/app/common/enums';
import { ModuleName } from 'src/app/subscription-package';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { ModulePermissionService } from 'src/app/subscription-package/services/module-permission.service';

export enum WorkerAgreementListType {
  WorkerAgreements = 1,
  EmploymentConditionsConfirmations = 2,
  MandateAgreementWorkedDays = 3,
  AnnexesList = 4,
}

export type TabName = 'worker-agreements' | 'employment-conditions-confirmations' | 'mandate-agreement-worked-days' | 'annex-registry';

@Component({
  selector: 'app-worker-agreement-list-tabs-container',
  templateUrl: './worker-agreement-list-tabs-container.component.html',
  styleUrls: ['./worker-agreement-list-tabs-container.component.scss'],
  standalone: false
})
export class WorkerAgreementListTabsContainerComponent implements OnInit {
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  listType: WorkerAgreementListType = WorkerAgreementListType.WorkerAgreements;
  listTypes = WorkerAgreementListType;
  selectedTabIndex = 0;
  public readonly mandateAgreementWorkedDayListPermissions: Permission[] = [Permission.ViewAllMandateAgreementWorkedDays, Permission.ViewMyExternalWorkerMandateAgreementWorkedDays];
  public readonly splitComponentId = 'WorkerAgreementListTabsContainerComponent';
  annexRegistryVisible = false;

  private readonly tabNameToListType: Record<TabName, WorkerAgreementListType> = {
    'worker-agreements': WorkerAgreementListType.WorkerAgreements,
    'employment-conditions-confirmations': WorkerAgreementListType.EmploymentConditionsConfirmations,
    'mandate-agreement-worked-days': WorkerAgreementListType.MandateAgreementWorkedDays,
    'annex-registry': WorkerAgreementListType.AnnexesList,
  };

  private readonly listTypeToTabName: Record<WorkerAgreementListType, TabName> = {
    [WorkerAgreementListType.WorkerAgreements]: 'worker-agreements',
    [WorkerAgreementListType.EmploymentConditionsConfirmations]: 'employment-conditions-confirmations',
    [WorkerAgreementListType.MandateAgreementWorkedDays]: 'mandate-agreement-worked-days',
    [WorkerAgreementListType.AnnexesList]: 'annex-registry',
  };

  private modulePermissionService = inject(ModulePermissionService);

  constructor() {
  }

  ngOnInit(): void {
    this.modulePermissionService.isAllowedModule(ModuleName.Annex).then(allowed => {
      this.annexRegistryVisible = allowed && this.authService.hasPermission(Permission.ViewAnnexRegistry);
      this.selectedTabIndex = this.getTabIndexForListType(this.listType);
    });
    this.route.queryParams.subscribe(params => {
      const tabParam = params['tab'] as TabName;
      if (tabParam && this.tabNameToListType[tabParam]) {
        this.listType = this.tabNameToListType[tabParam];
        this.selectedTabIndex = this.getTabIndexForListType(this.listType);
      } else {
        this.listType = this.getInitialListType();
        this.selectedTabIndex = 0;
      }
    });
  }

  private getTabIndexForListType(listType: WorkerAgreementListType): number {
    const visibleTabs = [
      WorkerAgreementListType.WorkerAgreements,
      this.hasPermissionToEmploymentConditionsConfirmations() ? WorkerAgreementListType.EmploymentConditionsConfirmations : null,
      this.hasPermissionToMandateAgreementWorkedDayList() ? WorkerAgreementListType.MandateAgreementWorkedDays : null,
      this.hasPermissionToAnnexRegistry() ? WorkerAgreementListType.AnnexesList : null,
    ].filter((t): t is WorkerAgreementListType => t !== null);

    const index = visibleTabs.indexOf(listType);
    return index >= 0 ? index : 0;
  }

  hasPermissionToEmploymentConditionsConfirmations(): boolean {
    return this.authService.hasPermission(Permission.ViewMyExternalWorkerEmploymentConditionsConfirmation) || this.authService.hasPermission(Permission.ViewAllExternalWorkerEmploymentConditionsConfirmation);
  }

  hasPermissionToMandateAgreementWorkedDayList(): boolean {
    return this.authService.hasAnyPermission(this.mandateAgreementWorkedDayListPermissions);
  }

  hasPermissionToAnnexRegistry(): boolean {
    return this.annexRegistryVisible;
  }

  onTabChanged(event: MatTabChangeEvent): void {
    const map = new Map<string, WorkerAgreementListType>([
      [this.translate.instant('WL-WorkerAgreement'), WorkerAgreementListType.WorkerAgreements],
      [this.translate.instant('EmploymentConditionsConfirmationList.EmploymentConditionsConfirmationList'), WorkerAgreementListType.EmploymentConditionsConfirmations],
      [this.translate.instant('MandateAgreementWorkedDayList.MandateAgreementWorkedDayList'), WorkerAgreementListType.MandateAgreementWorkedDays],
      [this.translate.instant('WL-AnnexesList'), WorkerAgreementListType.AnnexesList],
    ]);

    if (!map.has(event.tab.textLabel)) {
      return;
    }

    const newListType = map.get(event.tab.textLabel);
    this.listType = newListType;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.listTypeToTabName[newListType] },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private getInitialListType() {
    return WorkerAgreementListType.WorkerAgreements;
  }
}
